import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiMessageCircle, FiX, FiSend,
  FiFileText, FiBriefcase, FiClipboard, FiUpload, FiMic, FiHome,
  FiHelpCircle, FiMail, FiArrowLeft, FiStar, FiEdit3, FiSearch, FiUser
} from 'react-icons/fi';
import './QuickAssistant.css';

const TOPICS = {
  main: {
    greeting: "Hi! I'm your Resumemo assistant. How can I help you today?",
    options: [
      { id: 'resume', label: 'Resume Help', icon: <FiFileText />, color: '#6366f1' },
      { id: 'job', label: 'Job Search', icon: <FiBriefcase />, color: '#0ea5e9' },
      { id: 'navigate', label: 'Go to a page', icon: <FiSearch />, color: '#8b5cf6' },
      { id: 'support', label: 'Get Support', icon: <FiHelpCircle />, color: '#f59e0b' },
    ],
  },
  resume: {
    greeting: "What would you like to do with your resume?",
    options: [
      { id: 'nav', label: 'Upload a new resume', icon: <FiUpload />, color: '#6366f1', path: '/resumes/upload' },
      { id: 'nav', label: 'View my resumes', icon: <FiFileText />, color: '#8b5cf6', path: '/resumes' },
      { id: 'nav', label: 'Polish / tailor resume', icon: <FiStar />, color: '#ec4899', path: '/resumes' },
      { id: 'nav', label: 'Edit resume content', icon: <FiEdit3 />, color: '#10b981', path: '/resumes' },
    ],
  },
  job: {
    greeting: "Let's help with your job search!",
    options: [
      { id: 'nav', label: 'Save a new job', icon: <FiBriefcase />, color: '#0ea5e9', path: '/jobs/new' },
      { id: 'nav', label: 'View applications', icon: <FiClipboard />, color: '#10b981', path: '/applications' },
      { id: 'nav', label: 'Practice interviews', icon: <FiMic />, color: '#f59e0b', path: '/jobs/mock-interview' },
    ],
  },
  navigate: {
    greeting: "Where would you like to go?",
    options: [
      { id: 'nav', label: 'Dashboard', icon: <FiHome />, color: '#64748b', path: '/' },
      { id: 'nav', label: 'My Resumes', icon: <FiFileText />, color: '#8b5cf6', path: '/resumes' },
      { id: 'nav', label: 'Applications', icon: <FiClipboard />, color: '#10b981', path: '/applications' },
      { id: 'nav', label: 'My Profile', icon: <FiUser />, color: '#6366f1', path: '/settings' },
      { id: 'nav', label: 'Upload Resume', icon: <FiUpload />, color: '#ec4899', path: '/resumes/upload' },
      { id: 'nav', label: 'Mock Interview', icon: <FiMic />, color: '#f59e0b', path: '/jobs/mock-interview' },
    ],
  },
  support: {
    greeting: "Need help? Here are your options:",
    options: [
      { id: 'nav', label: 'Contact Us', icon: <FiMail />, color: '#ef4444', path: '/contact' },
      { id: 'nav', label: 'Send Feedback', icon: <FiSend />, color: '#8b5cf6', path: '/feedback' },
      { id: 'faq', label: 'Common Questions', icon: <FiHelpCircle />, color: '#0ea5e9' },
    ],
  },
  faq: {
    greeting: "Here are some frequently asked questions:",
    options: [
      { id: 'answer', label: 'How do I tailor my resume?', answer: 'Open any resume, click "Apply for Jobs", select a saved job, and our AI will tailor your resume to match the job description.' },
      { id: 'answer', label: 'How do I download my resume?', answer: 'Go to any resume, click "Download PDF" at the top. You can choose from multiple styles and also download as DOCX.' },
      { id: 'answer', label: 'How do I track applications?', answer: 'After applying for a job through Resumemo, it automatically appears in your Applications page. You can also manually add applications.' },
      { id: 'nav', label: 'Still need help? Contact us', icon: <FiMail />, color: '#ef4444', path: '/contact' },
    ],
  },
};

const QuickAssistant = () => {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(['main']);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const messagesEndRef = useRef(null);

  const currentTopic = history[history.length - 1];
  const topic = TOPICS[currentTopic] || TOPICS.main;
  const canGoBack = history.length > 1;

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentTopic, expandedIdx]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleOption = (option, idx) => {
    if (option.path) {
      setOpen(false);
      navigate(option.path);
    } else if (option.answer) {
      setExpandedIdx(prev => prev === idx ? null : idx);
    } else if (TOPICS[option.id]) {
      setExpandedIdx(null);
      setHistory(prev => [...prev, option.id]);
    }
  };

  const goBack = () => {
    setExpandedIdx(null);
    setHistory(prev => prev.length > 1 ? prev.slice(0, -1) : prev);
  };

  const handleOpen = () => {
    setOpen(true);
    setHistory(['main']);
    setExpandedIdx(null);
  };

  return (
    <div className="qa-root" ref={containerRef}>
      {open && (
        <div className="qa-chat" role="dialog" aria-label="Assistant">
          <div className="qa-chat-header">
            {canGoBack && (
              <button className="qa-back-btn" onClick={goBack} aria-label="Go back">
                <FiArrowLeft size={16} />
              </button>
            )}
            <div className="qa-chat-header-text">
              <span className="qa-chat-title">Resumemo Assistant</span>
              <span className="qa-chat-status">Online</span>
            </div>
            <button className="qa-close-btn" onClick={() => setOpen(false)} aria-label="Close">
              <FiX size={16} />
            </button>
          </div>

          <div className="qa-chat-body">
            <div className="qa-bubble qa-bubble--bot">
              {topic.greeting}
            </div>

            <div className="qa-options">
              {topic.options.map((opt, idx) => (
                <React.Fragment key={idx}>
                  <button
                    type="button"
                    className={'qa-option-btn' + (expandedIdx === idx ? ' qa-option-btn--active' : '')}
                    onClick={() => handleOption(opt, idx)}
                    aria-label={opt.label}
                    aria-expanded={opt.answer ? expandedIdx === idx : undefined}
                  >
                    {opt.icon && (
                      <span className="qa-option-icon" style={{ background: opt.color || '#6b7280' }}>
                        {opt.icon}
                      </span>
                    )}
                    <span className="qa-option-label">{opt.label}</span>
                    {opt.answer && (
                      <span className={'qa-chevron' + (expandedIdx === idx ? ' qa-chevron--open' : '')}>&#9662;</span>
                    )}
                  </button>
                  {opt.answer && expandedIdx === idx && (
                    <div className="qa-inline-answer">
                      {opt.answer}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div ref={messagesEndRef} />
          </div>

          <div className="qa-chat-footer">
            <button type="button" className="qa-footer-link" aria-label="Start over" onClick={() => { setExpandedIdx(null); setHistory(['main']); }}>
              Start over
            </button>
            <span className="qa-footer-sep">|</span>
            <button type="button" className="qa-footer-link" aria-label="Contact us" onClick={() => { setOpen(false); navigate('/contact'); }}>
              Contact us
            </button>
          </div>
        </div>
      )}

      <button
        className={'qa-trigger' + (open ? ' qa-trigger--open' : '')}
        onClick={() => open ? setOpen(false) : handleOpen()}
        aria-expanded={open}
        aria-label="Chat with assistant"
        title="Need help?"
      >
        {open ? <FiX size={22} /> : <FiMessageCircle size={22} />}
      </button>
    </div>
  );
};

export default QuickAssistant;
